// domain/model/Cap.js
import { isPeriodicCap, isResetBehavior } from '../constants/authorization';

/**
 * Cap — one ceiling the letter states on an approved amount.
 *
 * SEVERAL CAPS GOVERN ONE AMOUNT AND ARE NEVER ADDITIVE. "1,040 units total, and no more than 40
 * hours per week" is one pool with two independent ceilings; the weekly cap paces the total, it does
 * not add to it. Enforced structurally rather than by a rule — the limit carries no total of its own
 * for two caps to be summed into.
 *
 * NO UNREADABLE-VALUE GUARD, unlike `EffectivePeriod`. There an unreadable bound normalizes to null
 * which READS AS OPEN, turning a record that expires into one that never does, so validation has to
 * judge the raw input first. Here it normalizes to "no figure", which is already invalid and
 * surfaces as a required-field error. The asymmetry is in the domain, not in the diligence.
 *
 * @domainObject value-object
 * @invariant Frozen. A cap is replaced, never edited in place.
 * @invariant `resetBehavior` is null on a non-periodic cap, enforced here rather than validated: a
 *   window total draws down by definition, so a behavior stated on one has no meaning to record.
 * @invariant `figure` is a positive number. Zero units is a denial, not an approval.
 * @typedef {Object} Cap
 * @property {number|null} figure - the amount the letter states. Not money, so no minor-units rule
 *   applies, and fractions are ordinary ("2.5 hours").
 * @property {import('../constants/authorization').StatedUnit|null} statedUnit - the LETTER's unit,
 *   never the code's `unitBasis`. A letter saying "20 hours per week" against a code billing in
 *   15-minute units means 80 units on the claim; both facts are true, and converting here would
 *   destroy the only record of what was approved.
 * @property {import('../constants/authorization').CapPeriod|null} period - the calendar this
 *   ceiling applies over.
 * @property {import('../constants/authorization').ResetBehavior|null} resetBehavior - what happens
 *   to unused capacity at a period boundary. NULL HAS TWO READINGS, told apart by `period`:
 *   inapplicable on a total, not-recorded on a periodic cap. Nothing writes a non-null value today,
 *   so every stored cap reads as the second.
 */

/**
 * Builds a cap, normalizing each fact and freezing the result.
 *
 * An unrecognized `statedUnit` or `period` is PRESERVED rather than blanked, so validation can name
 * the offending value instead of reporting a field the caller can see they filled in.
 *
 * @param {Partial<Cap>} [attrs]
 * @returns {Cap} frozen
 */
// eslint-disable-next-line import/prefer-default-export
export function createCap(attrs = {}) {
  const period = attrs.period ?? null;

  return Object.freeze({
    figure: Number.isFinite(attrs.figure) ? attrs.figure : null,
    statedUnit: attrs.statedUnit ?? null,
    period,
    // Not a validation: the concept does not apply off a periodic cap, so there is nothing to
    // record and nothing to report. A behavior supplied here is dropped rather than refused.
    resetBehavior: isPeriodicCap(period) && isResetBehavior(attrs.resetBehavior)
      ? attrs.resetBehavior
      : null,
  });
} // createCap()
